<script setup>
/**
 * GlModal — accessible modal dialog.
 *
 * Forward-compatible with @gitlab/ui's GlModal:
 *   - v-model maps to `modelValue` prop + `update:modelValue` event
 *   - `visible` prop + `change` event also supported for backwards compatibility
 *   - `title`, `modalId`, `actionPrimary`, `actionCancel`, `size` match GlModal props
 *   - Emits `primary`, `canceled`, `change`, `update:modelValue` like the real GlModal
 *   - Slots: default (body), modal-header, modal-footer
 *
 * A future swap to <GlModal v-model="show" modal-id="..." title="..." ...>
 * requires only changing the `from` path.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { iconHref } from "~/utils/icons.js";

const BUTTON_VARIANT_CLASSES = {
  confirm: {
    primary:
      "border border-[var(--gl-button-confirm-secondary-border-color-default)] text-[var(--gl-button-confirm-secondary-foreground-color-default)] bg-[var(--gl-button-confirm-secondary-background-color-default)] hover:border-[var(--gl-button-confirm-secondary-border-color-hover)] hover:bg-[var(--gl-button-confirm-secondary-background-color-hover)] hover:text-[var(--gl-button-confirm-secondary-foreground-color-hover)] font-medium",
    secondary:
      "border border-[var(--gl-button-confirm-secondary-border-color-default)] text-[var(--gl-button-confirm-secondary-foreground-color-default)] bg-[var(--gl-button-confirm-secondary-background-color-default)] hover:border-[var(--gl-button-confirm-secondary-border-color-hover)] hover:bg-[var(--gl-button-confirm-secondary-background-color-hover)] hover:text-[var(--gl-button-confirm-secondary-foreground-color-hover)] font-medium"
  },
  danger: {
    primary:
      "border border-[var(--gl-button-danger-secondary-border-color-default)] text-[var(--gl-button-danger-secondary-foreground-color-default)] bg-[var(--gl-button-danger-secondary-background-color-default)] hover:border-[var(--gl-button-danger-secondary-border-color-hover)] hover:bg-[var(--gl-button-danger-secondary-background-color-hover)] hover:text-[var(--gl-button-danger-secondary-foreground-color-hover)] font-medium",
    secondary:
      "border border-[var(--gl-button-danger-secondary-border-color-default)] text-[var(--gl-button-danger-secondary-foreground-color-default)] bg-[var(--gl-button-danger-secondary-background-color-default)] hover:border-[var(--gl-button-danger-secondary-border-color-hover)] hover:bg-[var(--gl-button-danger-secondary-background-color-hover)] hover:text-[var(--gl-button-danger-secondary-foreground-color-hover)] font-medium"
  },
  default: {
    primary:
      "border border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)] hover:border-[var(--gl-border-color-strong)] hover:text-[var(--gl-text-color-default)] font-medium",
    secondary:
      "border border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)] hover:border-[var(--gl-border-color-strong)] hover:text-[var(--gl-text-color-default)] font-medium"
  }
};

const SIZE_CLASSES = {
  lg: "max-w-lg",
  md: "max-w-md",
  sm: "max-w-sm"
};

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const { actionCancel, actionPrimary, dismissLabel, modalId, modelValue, size, title, visible } = defineProps({
  /**
   * Cancel action button config.
   * Shape: { text: String, attributes?: Object }
   */
  actionCancel: { default: null, type: Object },
  /**
   * Primary action button config.
   * Shape: { text: String, attributes?: { variant?, category?, disabled?, loading? } }
   */
  actionPrimary: { default: null, type: Object },
  /** aria-label for the close button. */
  dismissLabel: { default: "Close", type: String },
  /** Unique modal identifier. */
  modalId: { required: true, type: String },
  /** Controls visibility via v-model. Takes precedence over `visible` when provided. */
  modelValue: { default: undefined, type: Boolean },
  /** Modal size: sm, md, or lg. */
  size: { default: "md", type: String },
  /** Title text displayed in the header. */
  title: { default: null, type: String },
  /** Controls visibility (v-model via `change` event). */
  visible: { default: false, type: Boolean }
});

const emit = defineEmits(["change", "update:modelValue", "primary", "canceled"]);

// modelValue takes precedence over visible when explicitly provided
const isVisible = computed(() => (modelValue !== undefined ? modelValue : visible));

const sizeClass = computed(() => SIZE_CLASSES[size] ?? SIZE_CLASSES.md);
const titleId = computed(() => `${modalId}-title`);

const dialogRef = ref(null);
const previouslyFocusedElement = ref(null);

function resolveButtonClasses(action, fallbackVariant, fallbackCategory) {
  const attrs = action?.attributes ?? {};
  const variant = attrs.variant ?? fallbackVariant;
  const category = attrs.category ?? fallbackCategory;
  const variantMap = BUTTON_VARIANT_CLASSES[variant] ?? BUTTON_VARIANT_CLASSES.default;
  return variantMap[category] ?? variantMap.primary;
}

const primaryClasses = computed(() => resolveButtonClasses(actionPrimary, "confirm", "primary"));
const cancelClasses = computed(() => resolveButtonClasses(actionCancel, "default", "primary"));

const primaryDisabled = computed(() => actionPrimary?.attributes?.disabled === true);
const primaryLoading = computed(() => actionPrimary?.attributes?.loading === true);
const primaryText = computed(() => {
  if (primaryLoading.value && actionPrimary?.attributes?.loadingText) {
    return actionPrimary.attributes.loadingText;
  }
  return actionPrimary?.text ?? "";
});

// Forward non-internal attributes (id, data-testid, etc.) to the primary button element
const INTERNAL_ATTRS = new Set(["variant", "category", "disabled", "loading", "loadingText"]);
const primaryPassthroughAttrs = computed(() => {
  const attrs = actionPrimary?.attributes;
  if (!attrs) return {};
  const result = {};
  for (const [key, val] of Object.entries(attrs)) {
    if (!INTERNAL_ATTRS.has(key)) result[key] = val;
  }
  return result;
});

function getFocusableElements() {
  if (!dialogRef.value) return [];
  return [...dialogRef.value.querySelectorAll(FOCUSABLE_SELECTOR)];
}

function setVisibility(value) {
  emit("change", value);
  emit("update:modelValue", value);
}

function close() {
  setVisibility(false);
}

function onPrimary(event) {
  emit("primary", event);
  if (!event.defaultPrevented) {
    close();
  }
}

function onCancel(event) {
  emit("canceled", event);
  close();
}

function onBackdropClick() {
  close();
}

function moveFocus(event, target) {
  event.preventDefault();
  target.focus();
}

function trapTabFocus(event) {
  const focusable = getFocusableElements();
  if (focusable.length === 0) {
    event.preventDefault();
    return;
  }

  const [first] = focusable;
  const [last] = focusable.slice(-1);

  if (event.shiftKey && document.activeElement === first) {
    moveFocus(event, last);
  } else if (!event.shiftKey && document.activeElement === last) {
    moveFocus(event, first);
  }
}

function onKeydown(event) {
  if (!isVisible.value) return;

  if (event.key === "Escape") {
    close();
    return;
  }

  if (event.key === "Tab") trapTabFocus(event);
}

watch(isVisible, async (nowVisible) => {
  document.body.style.overflow = nowVisible ? "hidden" : "";

  if (nowVisible) {
    previouslyFocusedElement.value = document.activeElement;
    await nextTick();
    const focusable = getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (dialogRef.value) {
      dialogRef.value.focus();
    }
  } else if (previouslyFocusedElement.value) {
    previouslyFocusedElement.value.focus();
    previouslyFocusedElement.value = null;
  }
});

onMounted(() => {
  document.addEventListener("keydown", onKeydown);
  if (isVisible.value) {
    document.body.style.overflow = "hidden";
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
  document.body.style.overflow = "";
});

defineExpose({ hide: close, show: () => setVisibility(true) });
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="modal-backdrop">
      <div v-if="isVisible" class="fixed inset-0 z-40 bg-black/50" aria-hidden="true" @click="onBackdropClick" />
    </Transition>

    <!-- Dialog -->
    <Transition name="modal-content">
      <div
        v-if="isVisible"
        :id="modalId"
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : undefined"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          :class="[
            'w-full rounded-[var(--gl-modal-border-radius)] bg-[var(--gl-background-color-default)] shadow-[var(--gl-shadow-lg)]',
            sizeClass
          ]"
          @click.stop
        >
          <!-- Header -->
          <slot name="modal-header">
            <div class="flex items-center justify-between border-b border-[var(--gl-border-color-default)] px-6 py-4">
              <h2 v-if="title" :id="titleId" class="text-base font-semibold text-[var(--gl-text-color-heading)]">
                {{ title }}
              </h2>
              <button
                type="button"
                :aria-label="dismissLabel"
                class="rounded-[var(--gl-action-border-radius)] p-1 text-[var(--gl-text-color-subtle)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)]"
                @click="close"
              >
                <svg width="16" height="16" aria-hidden="true">
                  <use :href="iconHref('close')" />
                </svg>
              </button>
            </div>
          </slot>

          <!-- Body -->
          <div class="px-6 py-5">
            <slot />
          </div>

          <!-- Footer -->
          <slot name="modal-footer" :cancel="onCancel" :primary="onPrimary">
            <div
              v-if="actionCancel || actionPrimary"
              class="flex justify-end gap-2 border-t border-[var(--gl-border-color-default)] px-6 py-4"
            >
              <button
                v-if="actionCancel"
                type="button"
                :class="[
                  'rounded-[var(--gl-action-border-radius)] px-4 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)]',
                  cancelClasses
                ]"
                @click="onCancel"
              >
                {{ actionCancel.text }}
              </button>
              <button
                v-if="actionPrimary"
                type="button"
                :disabled="primaryDisabled || primaryLoading"
                :class="[
                  'rounded-[var(--gl-action-border-radius)] px-4 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)] disabled:cursor-not-allowed disabled:opacity-50',
                  primaryClasses
                ]"
                v-bind="primaryPassthroughAttrs"
                @click="onPrimary"
              >
                {{ primaryText }}
              </button>
            </div>
          </slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}
.modal-content-enter-active,
.modal-content-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.modal-content-enter-from,
.modal-content-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
